import { Icon } from "@mdi/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { mdiLock } from "@mdi/js";
import classnames from "classnames";
import { EvaChart, TimeStep } from "constants/charts.enums";
import { useChartsContext } from "contexts";
import { UNDEFINED_LOCK_OPTION } from "entities/charts";
import { updateChartByPath } from "store/features";
import { LockState } from "types/charts/locks";

import { useChartEntities } from "hooks/charts";

import { DataEnum, Format } from "models";

import { AxisInput } from "./AxisInput";
import "./AxisLocker.scss";
import { LockIconWrapper } from "./AxisLocker.styles";

export function XMaxLocker({ onInputStateChange, xAxisIndex = 0 }) {
  const { id } = useChartsContext();
  const dispatch = useDispatch();
  const {
    options: optionString,
    locks,
    mpcLocks,
    multiProduct,
    chartType,
    forecast,
    timeStep
  } = useChartEntities(id);

  const isMultiProductActive = multiProduct?.active;

  // Keep a ref to mpcLocks so the options-sync effect always reads the latest value
  // without adding mpcLocks to its dep array (which would cause a dispatch loop when
  // override === true: dispatch → mpcLocks changes → effect re-runs → dispatch again).
  const mpcLocksRef = useRef(mpcLocks);
  mpcLocksRef.current = mpcLocks;

  const parsedAxis = useMemo(() => {
    const defaultReturn = {
      xAxisData: undefined,
      lockOptions: UNDEFINED_LOCK_OPTION,
      productKey: undefined
    };
    if (optionString.length === 0) return defaultReturn;
    try {
      const options = JSON.parse(optionString);
      const xAxisData = Array.isArray(options.xAxis)
        ? options.xAxis[xAxisIndex]
        : options.xAxis;
      const lockOptions = xAxisData?.lock ?? UNDEFINED_LOCK_OPTION;
      return {
        xAxisData,
        lockOptions,
        productKey: lockOptions?.productKey as string | undefined
      };
    } catch {
      return defaultReturn;
    }
  }, [optionString, xAxisIndex]);

  const [showLock, setShowLock] = useState(false);
  const [xMax, setXMax] = useState<LockState>(undefined);
  const [showInput, setShowInput] = useState(false);
  const timeCharts = [
    EvaChart.RateDate,
    EvaChart.RateDateMidstream,
    EvaChart.TotalRateDate,
    EvaChart.TotalRateDateMidstream,
    EvaChart.CAGR,
    EvaChart.DeclineRate,
    EvaChart.InjectorTotal
  ];
  const numberDateCharts = [EvaChart.RateTime, EvaChart.CumTime];
  const isForecastOn = forecast?.active && forecast?.enabled;

  // isLocked should be true if the xMax lock is set and is not a default lock or if the chart is a forecast chart
  const isLocked = useMemo(() => {
    if (isMultiProductActive && parsedAxis.productKey) {
      const axisMaxLock = mpcLocks?.x?.[parsedAxis.productKey]?.max;
      return axisMaxLock?.value !== undefined;
    }
    if (locks?.xMax && (locks.xMax.type !== DataEnum.DefaultDate || isForecastOn)) {
      return locks.xMax.value !== undefined;
    }
    return false;
  }, [locks?.xMax, mpcLocks, isMultiProductActive, parsedAxis, forecast?.active]);
  const isNotTimeChart = !timeCharts.includes(chartType);
  const isNumberTimeChart = numberDateCharts.includes(chartType);

  useEffect(() => {
    onInputStateChange?.(showInput && isNotTimeChart);
  }, [showInput, onInputStateChange]);

  useEffect(() => {
    if (optionString.length === 0) return;
    const { xAxisData, lockOptions, productKey } = parsedAxis;

    setShowLock(lockOptions.show);
    setXMax(lockOptions.max);
    const override = lockOptions?.max?.override ?? false;

    if (override && xAxisData?.max !== undefined) {
      if (isMultiProductActive && productKey) {
        const existingMax = mpcLocksRef.current?.x?.[productKey]?.max;
        if (existingMax?.type !== DataEnum.Reset) {
          dispatch(
            updateChartByPath({
              id,
              path: `mpcLocks.x.${productKey}.max`,
              value: { value: xAxisData.max, type: lockOptions.max.type }
            })
          );
        }
      } else if (locks?.xMax?.type !== DataEnum.Reset) {
        dispatch(
          updateChartByPath({
            id,
            path: `locks.xMax`,
            value: { value: xAxisData.max, type: lockOptions.max.type }
          })
        );
      }
    }
  }, [optionString, xAxisIndex, isMultiProductActive]);

  const handleLockClick = () => {
    if (isLocked) {
      if (isMultiProductActive && parsedAxis.productKey) {
        dispatch(
          updateChartByPath({
            id,
            path: `mpcLocks.x.${parsedAxis.productKey}.max`,
            value: { value: undefined, type: DataEnum.Reset }
          })
        );
      } else {
        dispatch(
          updateChartByPath({
            id,
            path: `locks.xMax`,
            value: { value: undefined, type: DataEnum.Reset }
          })
        );
      }
    } else {
      setShowInput(!showInput);
    }
  };

  const axisInputClassnames = classnames({ xAxis: true });
  const lockClassnames = classnames("axis-locker", "flex", {
    isLocked,
    xAxis: true,
    max: true
  });
  return (
    <>
      {showInput ? (
        <AxisInput
          axisInput={xMax}
          dataRange="xMax"
          lockPath={
            isMultiProductActive && parsedAxis.productKey
              ? `mpcLocks.x.${parsedAxis.productKey}.max`
              : undefined
          }
          setShowInput={setShowInput}
          className={classnames(axisInputClassnames, lockClassnames)}
          inputFormat={xMax?.format ?? Format.Number}
          chartType={chartType}
          unit={
            isNumberTimeChart
              ? timeStep?.properties?.value === TimeStep.Month ||
                timeStep?.properties?.value === TimeStep.Hybrid
                ? "months"
                : "days"
              : ""
          }
        />
      ) : (
        showLock && (
          <LockIconWrapper
            className={lockClassnames}
            onClick={handleLockClick}
            data-testid="x-max-locker">
            <Icon path={mdiLock} size={"16px"} />
          </LockIconWrapper>
        )
      )}
    </>
  );
}
